import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { getChapters } from '@/mocks/chapters';
import { formatViews } from '@/utils/format';

const ranked = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);
const flagship = comics.find((c) => c.id === 'tent-secret')!;
const flagshipChapters = getChapters('tent-secret');
const latestChapters = flagshipChapters.slice(-5);

export default function RankingSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
      <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
        {/* 人气榜 Top10 */}
        <div className="w-full lg:w-[440px] shrink-0">
          <div className="flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-display text-2xl md:text-3xl">
              <span className="text-primary-500">
                <i className="ri-fire-fill text-2xl md:text-3xl" aria-hidden="true" />
              </span>
              人气榜 TOP10
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
            </Link>
          </div>

          <ol className="mt-6 space-y-2">
            {ranked.map((comic, index) => (
              <li key={comic.id}>
                <Link
                  to={`/comic/${comic.id}`}
                  className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                >
                  <span
                    className={`w-7 shrink-0 text-center font-display text-lg ${
                      index === 0
                        ? 'text-primary-500'
                        : index === 1
                          ? 'text-accent-600'
                          : index === 2
                            ? 'text-secondary-600'
                            : 'text-foreground-400'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="relative h-12 w-9 shrink-0 overflow-hidden rounded-sm">
                    <img
                      src={comic.cover}
                      alt={`${comic.title}漫画封面`}
                      title={`${comic.title}漫画-免费阅读`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="mt-0.5 truncate text-xs text-foreground-600">
                      {comic.tags.slice(0, 2).join(' · ')}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs text-foreground-600">
                    <span className="hidden sm:inline-flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                      {comic.rating.toFixed(1)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true" />
                      {formatViews(comic.views)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </div>

        {/* 主打作品 */}
        <div className="w-full flex-1">
          <div className="flex items-end justify-between">
            <h2 className="flex items-center gap-2 font-display text-2xl md:text-3xl">
              <span className="text-accent-500">
                <i className="ri-star-smile-fill text-2xl md:text-3xl" aria-hidden="true" />
              </span>
              主编推荐 · 本周主打
            </h2>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg bg-background-100 hover-lift">
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
              <div className="relative aspect-[3/4] md:aspect-auto md:h-full overflow-hidden">
                <img
                  src={flagship.cover}
                  alt={`${flagship.title}漫画封面`}
                  title={`${flagship.title}漫画-免费阅读`}
                  className="h-full w-full object-cover object-top"
                />
                <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  本周主打
                </span>
              </div>
              <div className="flex flex-col justify-center p-5 md:p-7">
                <h3 className="font-display text-2xl md:text-3xl text-foreground-950">
                  《{flagship.title}》
                </h3>
                <p className="mt-1 text-xs text-foreground-600">
                  {flagship.author} · {flagship.originalTitle} · {flagship.year}年连载
                </p>
                <p className="mt-4 text-sm leading-6 md:leading-7 text-foreground-700 line-clamp-3">
                  {flagship.description}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {flagship.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5 rounded-md bg-background-50 p-3.5">
                  <p className="text-xs font-medium text-foreground-600">
                    最新更新 · 已更新至第{flagshipChapters.length}话
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {latestChapters.map((ch) => (
                      <li key={ch.id}>
                        <Link
                          to={`/comic/${flagship.id}/chapter/${ch.id}`}
                          className="flex items-center justify-between gap-3 text-sm text-foreground-800 hover:text-primary-600 transition-colors"
                        >
                          <span className="truncate">{ch.title}</span>
                          <span className="shrink-0 text-xs text-foreground-500">{ch.updatedAt}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${flagship.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-book-open-line text-base" aria-hidden="true" />
                    开始免费阅读
                  </Link>
                  <Link
                    to={`/comic/${flagship.id}/chapter/${flagshipChapters[0]?.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-secondary-600"
                  >
                    <i className="ri-play-circle-line text-base" aria-hidden="true" />
                    从第1话开始
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}